// Portal transition for menu jumps on the home: a circle opens from the click point,
// the cloud jumps to hyperspace, «Yendo a <section>» shows, the portal splits open
// and the page lands on the chapter's top, whose story restarts from step 1.
import { chapterTimelines } from './chapters'
import { $, G, motion } from './misc'

const LABELS: Record<string, string> = {
  '#top': 'Inicio', '#ia': 'Asesor IA', '#cotizador': 'Multicotizador', '#plata': 'Cobranzas y comisiones',
  '#producto': 'Producto', '#faq': 'Preguntas frecuentes', '#demo': 'Agendá una demo',
}

// The nav keeps real URLs (crawlable, and the right destination from any other page).
// On the home, the ones with an in-page chapter run the portal instead of navigating.
const IN_PAGE: Record<string, string> = {
  '/': '#top',
  '/asesor-ia': '#ia',
  '/multicotizador': '#cotizador',
  '/cobranzas': '#plata',
  '/#producto': '#producto',
  '/faq': '#faq',
}

let lastPt: [number, number] = [innerWidth / 2, innerHeight / 2]

export function goTo(sel: string, animated: boolean, moveFocus = false): void {
  const el = $(sel)
  if (!el) return
  // Keyboard users continue from the chapter they jumped to.
  const land = () => { if (!moveFocus) return; if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1'); el.focus({ preventScroll: true }) }
  const P = $('.portal'); const ring = $('.portal-ring'); const main = $('main')
  const lenis = motion.lenis
  if (!animated || !lenis || !P || !ring || !main) { el.scrollIntoView({ behavior: animated ? 'smooth' : 'auto' }); land(); return }
  if (P.dataset.busy) return
  P.dataset.busy = '1'
  const [x, y] = lastPt
  const lab = $('.portal-l', P)!; const bits = [$('svg', P), $('small', P), lab]
  lab.textContent = LABELS[sel] || ''
  G.set(P, { display: 'grid', clipPath: `circle(0% at ${x}px ${y}px)` })
  G.set(ring, { display: 'block', left: x, top: y, width: 0, height: 0, opacity: 1 })
  G.set(bits, { opacity: 0, y: 24, filter: 'blur(10px)' })
  // Zoom around the middle of the screen, not the middle of a page that is many screens tall.
  const focus = () => G.set(main, { transformOrigin: `50% ${scrollY + innerHeight / 2 - main.offsetTop}px` })
  focus()
  motion.gl?.warp()
  G.timeline({ onComplete: () => { G.set(P, { display: 'none' }); G.set(ring, { display: 'none' }); G.set(main, { clearProps: 'filter,scale,transformOrigin' }); delete P.dataset.busy; land() } })
    .to(main, { filter: 'blur(12px)', scale: 0.95, duration: 0.5, ease: 'power2.in' }, 0)
    .to(ring, { width: '240vmax', height: '240vmax', duration: 0.9, ease: 'expo.out' }, 0)
    .to(ring, { opacity: 0, duration: 0.4, ease: 'power1.in' }, 0.45)
    .to(P, { clipPath: `circle(150% at ${x}px ${y}px)`, duration: 0.62, ease: 'expo.in' }, 0.05)
    .to(bits, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.42, ease: 'power3.out', stagger: 0.06 }, 0.5)
    .add(() => {
      // Measure the target with the page at its real size, then zoom again for the landing.
      G.set(main, { scale: 1, filter: 'none' })
      lenis.scrollTo(el, { immediate: true, force: true })
      focus(); G.set(main, { scale: 1.05, filter: 'blur(12px)' })
      // Arriving from the menu always tells the chapter's story from its first step.
      // Rewind with events on, so the text typed by onUpdate callbacks is wiped too.
      const tl = chapterTimelines.get(el)
      if (tl) { tl.pause(); tl.seek(0, false); G.delayedCall(0.55, () => { tl.play() }) }
    }, 0.9)
    .to(bits, { opacity: 0, y: -24, filter: 'blur(10px)', duration: 0.3, ease: 'power2.in', stagger: 0.04 }, 1.22)
    .set(P, { clipPath: 'inset(0% 0% 0% 0%)' }, 1.3)
    .to(P, { clipPath: 'inset(50% 0% 50% 0%)', duration: 0.62, ease: 'expo.inOut' }, 1.32)
    .to(main, { filter: 'blur(0px)', scale: 1, duration: 0.8, ease: 'expo.out' }, 1.4)
    .add(() => { motion.gl?.pulse(innerWidth / 2, innerHeight / 2) }, 1.45)
}

/** Intercepts nav clicks on the home; modified or non-primary clicks navigate as usual. */
export function wirePortal(animated: boolean): void {
  if (location.pathname !== '/' || !$('.hero')) return
  addEventListener('pointerdown', (e) => { lastPt = [e.clientX, e.clientY] }, true)
  document.querySelectorAll<HTMLAnchorElement>('.nav a[href]').forEach((a) => {
    const sel = IN_PAGE[a.getAttribute('href') || '']
    if (!sel || !$(sel)) return
    a.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      e.preventDefault()
      // A keyboard "click" has no pointer: open the portal from the link itself.
      const keyboard = e.detail === 0
      if (keyboard) { const r = a.getBoundingClientRect(); lastPt = [r.left + r.width / 2, r.top + r.height / 2] }
      goTo(sel, animated, keyboard)
    })
  })
}
